import { useEffect } from 'react';
import { Info } from 'react-feather';
import './booking-edit-checkout.scss';
import {
  useCreditInUse,
  useCredits,
  useFetchCredits,
  useSetCreditInUse,
} from '../../../store/credits/creditsHooks';
import { useTranslation } from 'react-i18next';
import {
  useFetchSelectedActivityPayments,
  useSelectedActivityPayments,
} from '../../../store/my-activities/myActivitiesHooks';
import { getTheRightCredit } from '../../../utility/services/credits-calculate';
import { Credit } from '../../../store/CreditsManagement/creditsManagementTypes';
import { MyActivityPayment } from '../../../store/my-activities/myActivitiesTypes';

type PropsTypes = {
  creditTypes: CreditTypesEnum;
  price: number;
  discount?: number;
  disabledCreditUser?: boolean;
  isPurchasable?: boolean;
  isWaitingListMode?: boolean;
  activityId: number;
};

export enum CreditTypesEnum {
  COMPANY = 'COMPANY',
  USER = 'USER',
}

const BookingEditCheckout = (props: PropsTypes) => {
  const credits = useCredits();
  const { t } = useTranslation();
  const creditInUse = useCreditInUse();
  const fetchCredits = useFetchCredits();
  const setCreditInUse = useSetCreditInUse();
  const payments = useSelectedActivityPayments();
  const fetchPayments = useFetchSelectedActivityPayments();

  const costAfterDiscount =
    // eslint-disable-next-line no-mixed-operators
    props.price - props.price * (props?.discount ? props?.discount / 100 : 0);

  useEffect(() => {
    fetchPayments(props.activityId).finally();
    fetchCredits(props.creditTypes).finally();
  }, []);

  useEffect(() => {
    if (props.disabledCreditUser) {
      return;
    }

    const response = getTheRightCredit(
      calculateCreditsTotal(credits, payments),
      costAfterDiscount,
      payments
    );

    setCreditInUse(
      response.credits,
      response.creditInUse,
      response.balanceToPay
    );
  }, [props.price]);

  const calculateCreditsTotal = (
    credits: Credit[],
    payments: MyActivityPayment[]
  ): Credit[] => {
    const newCredits = credits.map((credit) => {
      const matchingPayment = payments.find(
        (payment) => payment.creditBalanceId === credit.id
      );
      if (matchingPayment) {
        const updatedUsage = credit.usage - matchingPayment.creditAmount;
        return { ...credit, usage: updatedUsage };
      } else {
        return credit;
      }
    });

    return newCredits;
  };

  const totalCreditAmount = payments.reduce((total, payment) => {
    return total + +(payment.creditAmount || 0);
  }, 0);

  const totalCashAmount = payments.reduce((total, payment) => {
    return total + +(payment.cashAmount || 0);
  }, 0);

  return (
    <div className="booking-checkout">
      <span className="total-cost">{t('NEW_TOTAL_COST')}</span>
      <div className="view-screen">
        <span className="booking-total">₪{costAfterDiscount}</span>
        <span className="original-price">₪{props.price}</span>
        <div className="discount_tag">
          {props.discount}% {t('OFF')}
        </div>
      </div>
      <div className="checkout-line" />
      <span className="total-cost">
        {t('PAYMENT_DIFFERENCE')}
        <span
          className="password-rules-container"
          id={`popBottom_${1}`}
          title={t('BOOKING_REFUND_TOOLTIP')}
        >
          <Info size={14} />
        </span>
      </span>
      <div className="credits-use">
        <span>{t('CREDITS_TAB_LABEL')}</span>
        {creditInUse?.creditsAmount ? (
          <span
            style={{
              direction: 'ltr',
              color:
                creditInUse?.creditsAmount - totalCreditAmount > 0
                  ? 'red'
                  : 'green',
            }}
          >
            {`${creditInUse?.creditsAmount - totalCreditAmount}`}
          </span>
        ) : (
          <span>0</span>
        )}
      </div>
      <div className="cash-use">
        <span>{t('CASH_TAB_LABEL')}</span>
        {creditInUse?.cashAmount ? (
          <>
            <span
              style={{
                direction: 'ltr',
                color:
                  creditInUse?.cashAmount - totalCashAmount > 0
                    ? 'red'
                    : 'green',
              }}
            >
              ₪{' '}
              {`${
                payments[0]?.sum
                  ? // eslint-disable-next-line no-mixed-operators
                    props.price -
                    // eslint-disable-next-line no-mixed-operators
                    props.price *
                      (props?.discount ? props?.discount / 100 : 0) -
                    payments[0]?.sum
                  : creditInUse?.cashAmount - totalCashAmount
              }`}
            </span>
          </>
        ) : (
          <span>₪0</span>
        )}
      </div>
      <div className="checkout-line" />
    </div>
  );
};

export default BookingEditCheckout;
